import { useQuery } from '@tanstack/react-query';
import { STALE_TIME } from 'infrastructure/consts/stale-time';
import { apiUrlGenerator } from 'utils/useTable';
import { CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE } from 'constants/clinicCareManagers';

import useApi from '../use-api';

import type { IClinicCareManager } from 'infrastructure/interfaces';

const ENDPOINT_URL = 'admins/admin-panel/clinics/care-managers';

export const CARE_MANAGERS_QUERY_KEY = 'careManagers';

const clinicCareManagersGetApi = (
  page: number,
  items: number = CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE,
  clinicGuid?: string,
) =>
  apiUrlGenerator(ENDPOINT_URL, items, page, {
    clinicGuid,
  });

export const getClinicCareManagersGetApiQueryKey = (
  page: number,
  items: number,
  clinicGuid?: string,
) => [CARE_MANAGERS_QUERY_KEY, clinicGuid, page, items];

interface IResponse {
  count: number;
  items: IClinicCareManager[];
}

interface IUseClinicCareManagersProps {
  clinicGuid?: string;
  page: number;
  items?: number;
  canLoad?: boolean;
}

interface IUseClinicCareManagers {
  careManagers: IResponse | undefined | void;
  loading: boolean;
}

export const useClinicCareManagers = (
  props: IUseClinicCareManagersProps,
): IUseClinicCareManagers => {
  const {
    clinicGuid,
    items = CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE,
    page = 1,
    canLoad = true,
  } = props;
  const { loadData } = useApi();

  const queryKey = getClinicCareManagersGetApiQueryKey(page, items, clinicGuid);

  const { data: careManagers, isFetching: loading } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await loadData<IResponse>(
        clinicCareManagersGetApi(page, items, clinicGuid),
      );

      return res;
    },
    enabled: Boolean(clinicGuid) && canLoad,
    staleTime: STALE_TIME,
  });

  return {
    careManagers,
    loading,
  };
};
